'use client';

import { useCallback, useMemo } from 'react';
import { SeedingItem, SeedingItemsResponse } from '@/app/(activity)/campaigns/[slug]/types';
import { getFirstPageAdpParam } from '@/api/marketingPageAdp';
import { useSWRInfiniteScroll } from '@/hooks/useSWRInfiniteScroll';
import { serializeKey } from '@/utils/swr/keys';

const SEEDING_ITEMS_URL = 'GET:/api/marketing_pages/seeding/items' as const;

function buildSeedingItemsRequestKey(
  marketingPageId: number,
  isPreview: boolean,
  page: number,
  pageSize: number,
  adp?: string,
) {
  return [
    SEEDING_ITEMS_URL,
    {
      params: {
        marketing_page_id: marketingPageId,
        page,
        page_size: pageSize,
        ...(isPreview ? { preview: 1 } : {}),
        ...getFirstPageAdpParam(page, adp),
      },
    },
  ] as const;
}

interface UseSeedingItemsOptions {
  marketingPageId: number;
  isPreview: boolean;
  initialData?: SeedingItemsResponse | null;
  pageSize?: number;
  adp?: string;
}

export function useSeedingItems({
  marketingPageId,
  isPreview,
  initialData,
  pageSize = 20,
  adp,
}: UseSeedingItemsOptions) {
  const firstPageKey = useMemo(() => {
    if (marketingPageId <= 0) return null;
    return serializeKey(buildSeedingItemsRequestKey(marketingPageId, isPreview, 1, pageSize, adp));
  }, [marketingPageId, isPreview, pageSize, adp]);

  const getKey = useCallback(
    (pageIndex: number, previousPageData: SeedingItemsResponse | null) => {
      if (marketingPageId <= 0) return null;
      if (previousPageData && !previousPageData.has_more) return null;
      return [
        ...buildSeedingItemsRequestKey(marketingPageId, isPreview, pageIndex + 1, pageSize, adp),
      ];
    },
    [marketingPageId, isPreview, pageSize, adp],
  );

  const fallbackData = useMemo(() => (initialData ? [initialData] : undefined), [initialData]);

  const {
    data: items,
    originalData,
    isLoading,
    isValidating,
    isReachingEnd,
    size,
    setSize,
    hasAttemptedFetch,
  } = useSWRInfiniteScroll<SeedingItem, SeedingItemsResponse>({
    firstPageKey,
    getKey,
    extractList: (page) => page?.items ?? [],
    checkIsReachingEnd: (page) => !page?.has_more,
    inView: true,
    swrConfig: {
      fallbackData,
    },
  });

  const loadMore = useCallback(() => {
    if (!isReachingEnd && !isValidating) {
      setSize((current) => current + 1);
    }
  }, [isReachingEnd, isValidating, setSize]);

  const total = originalData?.[0]?.total ?? 0;
  const loading = isLoading || isValidating;

  return {
    items,
    loading,
    isLoading,
    isValidating,
    hasMore: !isReachingEnd,
    isReachingEnd,
    page: size,
    total,
    loadMore,
    hasAttemptedFetch,
  };
}
